import {
  BankDossier,
  BankDossierResponse,
  BankMilestones,
  bankMilestoneKeys,
} from './types';

const emptyMilestones = (): BankMilestones =>
  bankMilestoneKeys.reduce(
    (milestones, key) => ({ ...milestones, [key]: null }),
    {} as BankMilestones
  );

export const emptyBankDossier = (): BankDossier => ({
  identification: {
    bank_control_number: null,
    folder_number: null,
    request_number: null,
    request_ordinal: null,
    sub_status: null,
    channel: null,
  },
  financing: {
    operation_type: null,
    request_kind: null,
    purchase_sale_value: null,
    financing_value: null,
    ltv: null,
    property_value: null,
    original_proposal_number: null,
    previous_proposal_number: null,
    property_owner: null,
    proponent_name: null,
    proponent_document: null,
    uses_fgts: false,
    proponent_eligibility: null,
    credit_line_purpose: null,
  },
  appraisal_value: null,
  milestones: emptyMilestones(),
  appraisal_company: {
    escalated_engineer: null,
    revision_type: null,
    revision_responsible: null,
    additional_info: null,
  },
  requesting_bank: {
    engineering_department: null,
    area_analyst: null,
    manager_name: null,
    requires_bank_engineering_analysis: false,
  },
  address_extras: {
    street_type: null,
    floor: null,
    condominium_name: null,
    original_address: null,
    is_new_area: false,
  },
  registry_extras: {
    total_linked_units: null,
    linked_units: [],
  },
  characteristics_extras: {
    blocks_towers: null,
    is_new_construction: false,
    is_rural: false,
  },
  report_kind: {
    original_report_reference: null,
  },
  charges: { items: [], total_bank_charges: 0, total_fees: 0 },
  contacts: [],
  files: [],
  related_requests: [],
});

/**
 * avaliahub-api dossier → the `BankDossier` the screens render. A block the
 * bank never filled may arrive `null` (and its flags too), so every block is
 * laid over an empty one. Charges, files and related requests have no source
 * yet: they stay empty.
 */
export const toBankDossier = (response: BankDossierResponse): BankDossier => {
  const empty = emptyBankDossier();
  const financing = { ...empty.financing, ...response.financing };
  const requestingBank = {
    ...empty.requesting_bank,
    ...response.requesting_bank,
  };
  const addressExtras = { ...empty.address_extras, ...response.address_extras };
  const characteristics = {
    ...empty.characteristics_extras,
    ...response.characteristics_extras,
  };

  return {
    ...empty,
    identification: { ...empty.identification, ...response.identification },
    financing: { ...financing, uses_fgts: !!financing.uses_fgts },
    appraisal_value: response.appraisal_value ?? null,
    milestones: { ...empty.milestones, ...response.milestones },
    appraisal_company: {
      ...empty.appraisal_company,
      ...response.appraisal_company,
    },
    requesting_bank: {
      ...requestingBank,
      requires_bank_engineering_analysis:
        !!requestingBank.requires_bank_engineering_analysis,
    },
    address_extras: {
      ...addressExtras,
      is_new_area: !!addressExtras.is_new_area,
    },
    registry_extras: {
      total_linked_units: response.registry_extras?.total_linked_units ?? null,
      linked_units: response.registry_extras?.linked_units ?? [],
    },
    characteristics_extras: {
      ...characteristics,
      is_new_construction: !!characteristics.is_new_construction,
      is_rural: !!characteristics.is_rural,
    },
    report_kind: { ...empty.report_kind, ...response.report_kind },
    contacts: response.contacts ?? [],
  };
};
